import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Form, Input, DatePicker, Button, Divider } from 'antd';
import FormValidationHelper from 'utils/validation';
import FileUpload from 'components/form/FileUpload';
import LicenseImg from 'components/LicenseImg';
import StateSelect from 'components/form/StateSelect';
import s from './s.module.less';
import { ILicenseData, LicenseData } from 'types/newUniprofile';
import dayjs from 'dayjs';
import { ELicenseType } from 'types/common';
import { formatDate } from 'utils/common';
import FileUploadPrivate from 'components/form/FileUploadPrivate';
import { isEqual } from 'lodash';
interface Props {
    initValue?: ILicenseData[];
    isEditing?: boolean;
}

const ControlledSubstanceCertification = forwardRef((props: Props, ref) => {
    const { isEditing, initValue } = props;
    const [form] = Form.useForm<{ licenses: LicenseData[] }>();
    const prevInitValueRef = useRef<ILicenseData[]>();

    useEffect(() => {
        if (initValue?.length && !isEqual(prevInitValueRef.current, initValue)) {
            prevInitValueRef.current = initValue;
            form.setFieldsValue({
                licenses: initValue.map((item) => ({
                    ...item,
                    expireDate: item.expireDate ? dayjs(item.expireDate) : undefined,
                })),
            });
        }
    }, [initValue]);

    const getValues = async () => {
        const values = await form.validateFields();
        return values.licenses.map((item) => ({
            ...item,
            expireDate: item.expireDate ? formatDate(item.expireDate) : undefined,
            type: ELicenseType.CONTROLLED_SUBSTAN_CECERTIFICATION,
        }));
    };

    useImperativeHandle(ref, () => ({
        getValues,
    }));

    const renderEditing = () => {
        return (
            <Form
                form={form}
                initialValues={{ remember: true }}
                autoComplete="off"
                layout="vertical"
            >
                <Form.List name="licenses" initialValue={[{}]}>
                    {(fields, { add, remove }) => {
                        const { length } = fields;
                        return (
                            <div id="cscLicense">
                                {fields.map((field, index) => {
                                    return (
                                        <Form.Item
                                            key={field.key}
                                            noStyle
                                        >
                                            <div className={s.subModule}>
                                                <div className={s.title}>Controlled Substance Certification {index + 1}</div>
                                                <Divider style={{ margin: '6px 0 24px' }} />
                                                <Form.Item
                                                    style={{ width: '320px' }}
                                                    label="License State"
                                                    name={[field.name, 'state']}
                                                    validateFirst
                                                    rules={[
                                                        { required: true, message: 'State is required' },
                                                    ]}
                                                >
                                                    <StateSelect />
                                                </Form.Item>
                                                <div className="flex gap-4">
                                                    <Form.Item
                                                        style={{ width: '320px' }}
                                                        label="CS license number"
                                                        name={[field.name, 'licenseNumber']}
                                                        rules={[
                                                            { required: true, message: 'CSC number is required' },
                                                            FormValidationHelper.validateLicenseNumber('License number must contain only letters or numbers'),
                                                        ]}
                                                        validateFirst
                                                    >
                                                        <Input maxLength={25} placeholder="What is the CS license number?" />
                                                    </Form.Item>

                                                    <Form.Item
                                                        style={{ width: '320px' }}
                                                        label="Expiration date"
                                                        name={[field.name, 'expireDate']}
                                                        rules={[{ required: true, message: 'Expiration date is required' }]}
                                                    >
                                                        <DatePicker
                                                            disabledDate={(date) => {
                                                                const now = (new Date()).getTime();
                                                                if (!date) {
                                                                    return false;
                                                                }
                                                                return date?.valueOf() < now;
                                                            }}
                                                            style={{ width: '100%' }}
                                                        />
                                                    </Form.Item>
                                                </div>
                                                <Form.Item
                                                    style={{ width: '300px' }}
                                                    label="License copy"
                                                    name={[field.name, 'url']}
                                                    rules={[{ required: true, message: 'License copy is required' }]}
                                                >
                                                    <FileUploadPrivate
                                                        uploadType="LICENSE"
                                                    />
                                                    {/* <FileUpload /> */}
                                                </Form.Item>

                                                {field.name !== 0 &&
                                                    <div className={s.removeWrap}>
                                                        <Button
                                                            onClick={() => remove(index)}
                                                            danger
                                                            size="small"
                                                        >Remove
                                                        </Button>
                                                    </div>}
                                            </div>
                                            {field.name === length - 1 &&
                                                <div
                                                    onClick={() => {
                                                        add();
                                                        const wrap = document.getElementById('cscLicense');
                                                        setTimeout(() => {
                                                            wrap?.parentElement?.parentElement?.scroll({
                                                                top: 1000000,
                                                                behavior: 'smooth',
                                                            });
                                                        }, 200);
                                                    }}
                                                    className={s.add}
                                                >
                                                    + Add another CSC license
                                                </div>
                                            }
                                        </Form.Item>);
                                })}
                            </div>);
                    }}
                </Form.List>
            </Form>

        );
    };

    const renderReview = () => {
        const licensesArray: ILicenseData[] = initValue?.filter((item: ILicenseData) => !!(item && item.licenseNumber)) ?? [];
        if (!licensesArray?.length) {
            return (
                <div className="mt-6">
                    <div className={s.title}>Controlled Substance Certification</div>
                    <div className="mt-3">Not provided</div>
                </div>
            );
        }
        return (
            <div className="mt-6">
                <div className={s.title}>Controlled Substance Certification</div>
                <Divider style={{ margin: '6px 0 24px' }} />
                {
                    licensesArray?.map((item: ILicenseData) => {
                        return (
                            <div className={s.subModule} key={item.licenseNumber}>
                                <div className={s.row}>
                                    <div
                                        style={{
                                            width: '300px',
                                        }}
                                        className={s.displayItem}
                                    >
                                        <div className={s.label}>CS License state</div>
                                        <div className={s.value}>{item.state}</div>
                                    </div>
                                </div>
                                <div className={s.row}>
                                    <div
                                        style={{
                                            width: '300px',
                                        }}
                                        className={s.displayItem}
                                    >
                                        <div className={s.label}>CS License number</div>
                                        <div className={s.value}>{item.licenseNumber}</div>
                                    </div>
                                    <div className={s.displayItem}>
                                        <div className={s.label}>Expiration date</div>
                                        <div className={s.value}>{item.expireDate ? dayjs(item.expireDate).format('MM-DD-YYYY') : ''}</div>
                                    </div>
                                </div>
                                <div className={s.row}>
                                    <div
                                        style={{
                                            width: '80%',
                                        }}
                                        className={s.displayItem}
                                    >
                                        <div className={s.label}>Uploaded license</div>
                                        <div className={s.value}>
                                            <LicenseImg src={item.fullUrl || item.url} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                }
            </div>
        );
    };

    return (
        <div className="mb-4">
            {isEditing ? renderEditing() : renderReview()}
        </div>
    );
});

export default ControlledSubstanceCertification;
